import React from 'react';

import { Placement } from '@popperjs/core';
import PropTypes from 'prop-types';

import {
  StylableComponent,
  stylePropType,
  variantSwitch,
} from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import {
  PLACEMENTS,
  VARIANTS,
  getMainAxisPlacement,
  ThemeVariant,
} from '../consts';

type ContainerProps = StylableComponent<HTMLDivElement> & {
  placement?: Placement;
  variant?: ThemeVariant;
  padding?: string | number;
};

const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  (
    { className, style, children, placement, variant, padding = '0', ...props },
    ref
  ) => (
    <div
      ref={ref}
      className={className}
      css={[
        {
          position: 'absolute',
          left: 0,
          top: 0,
          margin: 0,
          right: 'auto',
          bottom: 'auto',
          zIndex: theme.zIndex.tooltip,
        },
        variantSwitch({
          statement: getMainAxisPlacement(placement),
          variants: {
            [PLACEMENTS.LEFT]: {
              padding: `0 ${padding}`,
            },
            [PLACEMENTS.RIGHT]: {
              padding: `0 ${padding}`,
            },
            [PLACEMENTS.TOP]: {
              padding: `${padding} 0`,
            },
            [PLACEMENTS.BOTTOM]: {
              padding: `${padding} 0`,
            },
          },
        }),
        variantSwitch({
          statement: variant,
          variants: {
            [VARIANTS.LIGHT]: {
              color: theme.colors.charcoal900,
              backgroundColor: theme.colors.white,
              borderRadius: 4,
              boxShadow: theme.shadows.highElevation,
            },
            [VARIANTS.DARK]: {
              color: theme.colors.white,
              backgroundColor: theme.colors.charcoal900,
              borderRadius: 2,
            },
          },
        }),
        style,
      ]}
      {...props}
    >
      {children}
    </div>
  )
);

Container.displayName = 'Container';

Container.defaultProps = {
  padding: '0',
};

Container.propTypes = {
  /** className */
  className: PropTypes.string,
  /** style */
  style: stylePropType,
  /** placement */
  placement: PropTypes.oneOf(Object.values(PLACEMENTS)),
  /** children */
  children: PropTypes.node,
  /** padding */
  padding: PropTypes.string,
  /** variants */
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
};

export default Object.assign(Container, { PLACEMENTS, VARIANTS });
